import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, X } from 'lucide-react';
import { getallGraphdata } from '../../FeatureRedux/GraphData/showGraphdata';
import { useDispatch, useSelector } from 'react-redux';
import AddSector_Graph from './GraphComponent/addSector_Graph';
import { deleteGraph } from '../../FeatureRedux/GraphData/deleteGraphSection';
import { Graphupdate } from '../../FeatureRedux/GraphData/updateGraphSection';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const GraphTable = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editData, setEditData] = useState(null);
  const itemsPerPage = 5;

  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((s) => s.getallGraphdata);
  const { isloading } = useSelector((state) => state.deleteGraph);

  useEffect(() => {
    dispatch(getallGraphdata());
  }, [dispatch]);

  const graphData = Array.isArray(data) ? data : [];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = graphData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(graphData.length / itemsPerPage) || 1;

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  function handleClosemodalMaster() {
    setIsFormOpen(false);
    setEditData(null);
  }

  const handleEdit = (id) => {
    const itemToEdit = graphData.find(item => item._id === id);
    if (itemToEdit) {
      setEditData(itemToEdit);
      setIsFormOpen(true);
    }
  };




  const handleUpdate = async (id, updatedData) => {
    try {
      await dispatch(Graphupdate({ id, updatedData })).unwrap();
      toast.success('Sector updated successfully!', {
        position: 'top-center',
        autoClose: 4000,
      });
      setIsFormOpen(false);
      setEditData(null);
      dispatch(Graphupdate());
    } catch (error) {
      toast.error(error || 'Failed to update sector.', {
        position: 'top-center',
        autoClose: 4000,
      });
    }
  };
  

  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteGraph(itemToDelete)).unwrap();
      toast.success('Sector deleted successfully!', {
        position: 'top-center',
        autoClose: 4000,
      });
      dispatch(getallGraphdata());
    } catch (error) {
      toast.error(error || 'Failed to delete sector. Please try again.', {
        position: 'top-center',
        autoClose: 4000,
      });
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  return (
    <div className="flex justify-center p-4 relative">
      <ToastContainer />
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
              <button onClick={cancelDelete} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-300 mb-6">Are you sure you want to delete this sector? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition border border-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
                disabled={isloading}
              >
                {isloading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-5xl bg-gray-900 text-white rounded-xl shadow-lg border border-gray-700 overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700 bg-gray-800">
          <h2 className="text-xl font-semibold">Government Spending Sectors</h2>
          <button
            onClick={() => {
              setEditData(null);
              setIsFormOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition font-medium"
          >
            Add Sector
          </button>
        </div>

        {isFormOpen && (
          <AddSector_Graph 
            handleCloseModal={handleClosemodalMaster} 
            editData={editData}
            isEditMode={!!editData}
            onUpdate={handleUpdate}
          />
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-4 text-center">Loading...</div>
          ) : error ? (
            <div className="p-4 text-red-500 text-center">Error loading data.</div>
          ) : (
            <table className="w-full min-w-[600px] text-sm border-collapse">
              <thead className="bg-gray-800 border-b border-gray-700 text-left">
                <tr>
                  <th className="p-4 font-semibold">Sector</th>
                  <th className="p-4 font-semibold">Cost (in billions)</th>
                  <th className="p-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((row, index) => (
                  <tr
                    key={row._id}
                    className={`transition hover:bg-gray-800 border-b border-gray-700 ${index === currentItems.length - 1 ? 'border-none' : ''}`}
                  >
                    <td className="p-4">{row.sector}</td>
                    <td className="p-4">{row.cost}</td>
                    <td className="p-4 flex space-x-3">
                      <button
                        onClick={() => handleEdit(row._id)}
                        className="text-blue-400 hover:text-blue-300 transition"
                        title="Edit"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(row._id)}
                        className="text-red-400 hover:text-red-300 transition"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {graphData.length > 0 && (
          <div className="flex justify-between items-center p-4 border-t border-gray-700 bg-gray-800 text-sm">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-md bg-gray-700 text-white hover:bg-gray-600 transition disabled:opacity-40"
            >
              Previous
            </button>
            <span>
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-md bg-gray-700 text-white hover:bg-gray-600 transition disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GraphTable;

// import React, { useState, useEffect } from 'react';
// import { Pencil, Trash2, X } from 'lucide-react';
// import { getallGraphdata } from '../../FeatureRedux/GraphData/showGraphdata';
// import { useDispatch, useSelector } from 'react-redux';
// import AddSector_Graph from './GraphComponent/addSector_Graph';
// import { addGraphSection } from '../../FeatureRedux/GraphData/addGraphSection';
// import { deleteGraph } from '../../FeatureRedux/GraphData/deleteGraphSection';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import axios from 'axios';
// import axiosInstance from '../axiosInstance';

// const GraphTable = () => {
//   const [isFormOpen, setIsFormOpen] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [itemToDelete, setItemToDelete] = useState(null);
//   const [editData, setEditData] = useState(null);
//   const itemsPerPage = 5;

//   const dispatch = useDispatch();
//   const { data, loading, error } = useSelector((s) => s.getallGraphdata);
//   const {isloading} = useSelector((state) => state.deleteGraph)

//   useEffect(() => {
//     dispatch(getallGraphdata());
//   }, [dispatch]);

//   const graphData = Array.isArray(data) ? data : [];

//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = graphData.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(graphData.length / itemsPerPage) || 1;

//   const handlePageChange = (pageNumber) => {
//     if (pageNumber > 0 && pageNumber <= totalPages) {
//       setCurrentPage(pageNumber);
//     }
//   };

//   function handleClosemodalMaster() {
//     setIsFormOpen(false);
//     setEditData(null);
//   }

//   const handleEdit = (id) => {
//     const itemToEdit = graphData.find(item => item._id === id);
//     console.log('this is sls',id)
//     if (itemToEdit) {
//       setEditData(itemToEdit);
//       setIsFormOpen(true);
//     }
//   };

//   const handleDeleteClick = (id) => {
//     setItemToDelete(id);
//     setShowDeleteModal(true);
//   };

//   const confirmDelete = async () => {
//     try {
//       // Use the Redux thunk action instead of direct axios call
//       await dispatch(deleteGraph(itemToDelete)).unwrap();
      
//       toast.success('Sector deleted successfully!', {
//         position: 'top-center',
//         autoClose: 4000,
//       });
      
//       // Refresh the data after deletion
//       dispatch(getallGraphdata());
//     } catch (error) {
//       toast.error(error || 'Failed to delete sector. Please try again.', {
//         position: 'top-center',
//         autoClose: 4000,
//       });
//     } finally {
//       setShowDeleteModal(false);
//       setItemToDelete(null);
//     }
//   };

//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setItemToDelete(null);
//   };

//   return (
//     <div className="flex justify-center p-4 relative">
//       <ToastContainer />
//       {showDeleteModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 shadow-lg">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button onClick={cancelDelete} className="text-gray-400 hover:text-white">
//                 <X size={20} />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">Are you sure you want to delete this sector? This action cannot be undone.</p>
//             <div className="flex justify-end gap-3">
//               <button
//                 onClick={cancelDelete}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//                 disabled={isloading}
//               >
//                 {isloading ? (
//                   <>
//                     <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     Deleting...
//                   </>
//                 ) : (
//                   <>
//                     <Trash2 className="w-4 h-4 mr-2" />
//                     Delete
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="w-full max-w-5xl bg-gray-900 text-white rounded-xl shadow-lg border border-gray-700 overflow-hidden">
//         <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700 bg-gray-800">
//           <h2 className="text-xl font-semibold">Government Spending Sectors</h2>
//           <button
//             onClick={() => {
//               setEditData(null);
//               setIsFormOpen(true);
//             }}
//             className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition font-medium"
//           >
//             Add Sector
//           </button>
//         </div>

//         {isFormOpen && (
//           <AddSector_Graph 
//             handleCloseModal={handleClosemodalMaster} 
//             editData={editData}
//             isEditMode={!!editData}
//           />
//         )}

//         <div className="overflow-x-auto">
//           {loading ? (
//             <div className="p-4 text-center">Loading...</div>
//           ) : error ? (
//             <div className="p-4 text-red-500 text-center">Error loading data.</div>
//           ) : (
//             <table className="w-full min-w-[600px] text-sm border-collapse">
//               <thead className="bg-gray-800 border-b border-gray-700 text-left">
//                 <tr>
//                   <th className="p-4 font-semibold">Sector</th>
//                   <th className="p-4 font-semibold">Cost (in billions)</th>
//                   <th className="p-4 font-semibold">Actions</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {currentItems.map((row, index) => (
//                   <tr
//                     key={row._id}
//                     className={`transition hover:bg-gray-800 border-b border-gray-700 ${index === currentItems.length - 1 ? 'border-none' : ''}`}
//                   >
//                     <td className="p-4">{row.sector}</td>
//                     <td className="p-4">{row.cost}</td>
//                     <td className="p-4 flex space-x-3">
//                       <button
//                         onClick={() => handleEdit(row._id)}
//                         className="text-blue-400 hover:text-blue-300 transition"
//                         title="Edit"
//                       >
//                         <Pencil size={18} />
//                       </button>
//                       <button
//                         onClick={() => handleDeleteClick(row._id)} 
//                         className="text-red-400 hover:text-red-300 transition"
//                         title="Delete"
//                       >
//                         <Trash2 size={18} />
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           )}
//         </div>

//         {graphData.length > 0 && (
//           <div className="flex justify-between items-center p-4 border-t border-gray-700 bg-gray-800 text-sm">
//             <button
//               onClick={() => handlePageChange(currentPage - 1)}
//               disabled={currentPage === 1}
//               className="px-4 py-2 rounded-md bg-gray-700 text-white hover:bg-gray-600 transition disabled:opacity-40"
//             >
//               Previous
//             </button>
//             <span>
//               Page <strong>{currentPage}</strong> of {totalPages}
//             </span>
//             <button
//               onClick={() => handlePageChange(currentPage + 1)}
//               disabled={currentPage === totalPages}
//               className="px-4 py-2 rounded-md bg-gray-700 text-white hover:bg-gray-600 transition disabled:opacity-40"
//             >
//               Next
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default GraphTable;
// import React, { useState, useEffect } from 'react';
// import { Pencil, Trash2, X, Check } from 'lucide-react';
// import { getallGraphdata } from '../../FeatureRedux/GraphData/showGraphdata';
// import { useDispatch, useSelector } from 'react-redux';
// import AddSector_Graph from './GraphComponent/addSector_Graph';

// const GraphTable = () => {
//   const [isFormOpen, setIsFormOpen] = useState(false);
//   const [editIndex, setEditIndex] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [itemToDelete, setItemToDelete] = useState(null);
//   const itemsPerPage = 5;

//   const dispatch = useDispatch();
//   const { data, loading, error } = useSelector((s) => s.getallGraphdata);

//   useEffect(() => {
//     dispatch(getallGraphdata());
//   }, [dispatch]);

//   const graphData = Array.isArray(data) ? data : [];

//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = graphData.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(graphData.length / itemsPerPage) || 1;

//   const handlePageChange = (pageNumber) => {
//     if (pageNumber > 0 && pageNumber <= totalPages) {
//       setCurrentPage(pageNumber);
//     }
//   };

//   function handleClosemodalMaster() {
//     setIsFormOpen(false);
//   }

//   const handleEdit = (id) => {
//     // Placeholder for edit functionality
//     console.log('Edit item with id:', id);
//   };

//   const handleDeleteClick = (id) => {
//     setItemToDelete(id);
//     setShowDeleteModal(true);
//   };

//   const confirmDelete = () => {
//     console.log('Delete confirmed for item:', itemToDelete);
//     setShowDeleteModal(false);
//     setItemToDelete(null);
//   };

//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setItemToDelete(null);
//   };

//   return (
//     <div className="flex justify-center p-4 relative">
//       {/* Delete Confirmation Modal */}
//       {showDeleteModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 shadow-lg">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button onClick={cancelDelete} className="text-gray-400 hover:text-white">
//                 <X size={20} />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">Are you sure you want to delete this sector? This action cannot be undone.</p>
//             <div className="flex justify-end gap-3">
//               <button
//                 onClick={cancelDelete}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition border border-red-700 flex items-center"
//               >
//                 <Trash2 className="w-4 h-4 mr-1" />
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="w-full max-w-5xl bg-gray-900 text-white rounded-xl shadow-lg border border-gray-700 overflow-hidden">
//         <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700 bg-gray-800">
//           <h2 className="text-xl font-semibold">Government Spending Sectors</h2>
//           <button
//             onClick={() => setIsFormOpen(true)}
//             className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition font-medium"
//           >
//             Add Sector
//           </button>
//         </div>

//         {isFormOpen && <AddSector_Graph handleCloseModal={handleClosemodalMaster} />}

//         {/* Table */}
//         <div className="overflow-x-auto">
//           {loading ? (
//             <div className="p-4 text-center">Loading...</div>
//           ) : error ? (
//             <div className="p-4 text-red-500 text-center">Error loading data.</div>
//           ) : (
//             <table className="w-full min-w-[600px] text-sm border-collapse">
//               <thead className="bg-gray-800 border-b border-gray-700 text-left">
//                 <tr>
//                   <th className="p-4 font-semibold">Sector</th>
//                   <th className="p-4 font-semibold">Cost (in billions)</th>
//                   <th className="p-4 font-semibold">Actions</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {currentItems.map((row, index) => (
//                   <tr
//                     key={row._id}
//                     className={`transition hover:bg-gray-800 border-b border-gray-700 ${index === currentItems.length - 1 ? 'border-none' : ''}`}
//                   >
//                     <td className="p-4">{row.sector}</td>
//                     <td className="p-4">{row.cost}</td>
//                     <td className="p-4 flex space-x-3">
//                       <button
//                         onClick={() => handleEdit(row._id)}
//                         className="text-blue-400 hover:text-blue-300 transition"
//                         title="Edit"
//                       >
//                         <Pencil size={18} />
//                       </button>
//                       <button
//                         onClick={() => handleDeleteClick(row._id)}
//                         className="text-red-400 hover:text-red-300 transition"
//                         title="Delete"
//                       >
//                         <Trash2 size={18} />
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           )}
//         </div>

//         {/* Pagination */}
//         {graphData.length > 0 && (
//           <div className="flex justify-between items-center p-4 border-t border-gray-700 bg-gray-800 text-sm">
//             <button
//               onClick={() => handlePageChange(currentPage - 1)}
//               disabled={currentPage === 1}
//               className="px-4 py-2 rounded-md bg-gray-700 text-white hover:bg-gray-600 transition disabled:opacity-40"
//             >
//               Previous
//             </button>
//             <span>
//               Page <strong>{currentPage}</strong> of {totalPages}
//             </span>
//             <button
//               onClick={() => handlePageChange(currentPage + 1)}
//               disabled={currentPage === totalPages}
//               className="px-4 py-2 rounded-md bg-gray-700 text-white hover:bg-gray-600 transition disabled:opacity-40"
//             >
//               Next
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default GraphTable;



// import React, { useState, useEffect } from 'react';
// import { Pencil, Trash2, X, Check } from 'lucide-react';
// import { getallGraphdata } from '../../FeatureRedux/GraphData/showGraphdata';
// import { useDispatch, useSelector } from 'react-redux';
// import AddSector_Graph from './GraphComponent/addSector_Graph';

// const GraphTable = () => {
//   const [isFormOpen, setIsFormOpen] = useState(false);
//   const [newSector, setNewSector] = useState('');
//   const [newCost, setNewCost] = useState('');
//   const [editIndex, setEditIndex] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [itemToDelete, setItemToDelete] = useState(null);
//   const itemsPerPage = 5;

//   const dispatch = useDispatch();
//   const { data, loading, error } = useSelector((s) => s.getallGraphdata);

//   useEffect(() => {
//     dispatch(getallGraphdata());
//   }, [dispatch]);

//   // Ensure data is an array before using slice
//   const graphData = Array.isArray(data) ? data : [];

//   // Fixed pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = graphData.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(graphData.length / itemsPerPage) || 1; // Ensure at least 1 page

//   const handlePageChange = (pageNumber) => {
//     if (pageNumber > 0 && pageNumber <= totalPages) {
//       setCurrentPage(pageNumber);
//     }
//   };

//   function handleClosemodalMaster() {
//     setIsFormOpen(false);
//   }

//   const handleEdit = (id) => {
//     // Implement edit functionality here
//     // console.log('Edit item with id:', id);
//   };

//   const handleDeleteClick = (id) => {
//     setItemToDelete(id);
//     setShowDeleteModal(true);
//   };

//   const confirmDelete = () => {
//     // Implement actual delete functionality here
//     // console.log('Delete confirmed for item:', itemToDelete);
//     setShowDeleteModal(false);
//     setItemToDelete(null);
//   };

//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setItemToDelete(null);
//   };

//   return (
//     <div className="flex justify-center p-4 relative">
//       {/* Delete Confirmation Modal */}
//       {showDeleteModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-6 w-full max-w-md">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-bold text-gray-800">Confirm Deletion</h3>
//               <button onClick={cancelDelete} className="text-gray-500 hover:text-gray-700">
//                 <X size={20} />
//               </button>
//             </div>
//             <p className="text-gray-600 mb-6">Are you sure you want to delete this sector? This action cannot be undone.</p>
//             <div className="flex justify-end space-x-3">
//               <button
//                 onClick={cancelDelete}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition flex items-center"
//               >
//                 <Trash2 size={16} className="mr-2" />
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="max-w-4xl w-full rounded-3xl shadow-lg bg-gray-100 overflow-hidden">
//         <div className="flex justify-between items-center p-4 bg-gray-200">
//           <h2 className="text-xl font-bold text-gray-800">Government Spending Sectors</h2>
//           <button
//             onClick={() => setIsFormOpen(true)}
//             className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
//           >
//             Add Sector
//           </button>
//         </div>
//         {isFormOpen && <AddSector_Graph handleCloseModal={handleClosemodalMaster} />}

//         {/* Table */}
//         <div className="p-4">
//           {loading ? (
//             <p>Loading...</p>
//           ) : error ? (
//             <p className="text-red-500">Error loading data.</p>
//           ) : (
//             <table className="w-full text-left">
//               <thead>
//                 <tr className="bg-gray-200">
//                   <th className="p-4 text-gray-800 font-bold">Sector</th>
//                   <th className="p-4 text-gray-800 font-bold">Cost (in billions)</th>
//                   <th className="p-4 text-gray-800 font-bold">Actions</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {currentItems.map((row) => (
//                   <tr key={row._id} className="border-b border-gray-300 last:border-none hover:bg-gray-50">
//                     <td className="p-4 text-gray-800">{row.sector}</td>
//                     <td className="p-4 text-gray-800">{row.cost}</td>
//                     <td className="p-4 flex space-x-2">
//                       <button
//                         onClick={() => handleEdit(row._id)}
//                         className="text-blue-500 hover:text-blue-700 transition"
//                         title="Edit"
//                       >
//                         <Pencil size={18} />
//                       </button>
//                       <button
//                         onClick={() => handleDeleteClick(row._id)}
//                         className="text-red-500 hover:text-red-700 transition"
//                         title="Delete"
//                       >
//                         <Trash2 size={18} />
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           )}
//         </div>

//         {/* Pagination */}
//         {graphData.length > 0 && (
//           <div className="flex justify-between items-center p-4 bg-gray-200">
//             <button
//               onClick={() => handlePageChange(currentPage - 1)}
//               disabled={currentPage === 1}
//               className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
//             >
//               Previous
//             </button>
//             <span className="text-gray-800">
//               Page {currentPage} of {totalPages}
//             </span>
//             <button
//               onClick={() => handlePageChange(currentPage + 1)}
//               disabled={currentPage === totalPages}
//               className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
//             >
//               Next
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default GraphTable;


// use this code if have any issue....... 
// import React, { useState, useEffect } from 'react';
// import { Pencil, Trash2, X, CheckCircle, Edit } from 'lucide-react';
// import { getallGraphdata } from '../../FeatureRedux/GraphData/showGraphdata';
// import { useDispatch, useSelector } from 'react-redux';
// import AddSector_Graph from './GraphComponent/addSector_Graph';
// // import { addGraphSection } from '../../FeatureRedux/GraphData/addGraphSection';

// const GraphTable = () => {
//   const [isFormOpen, setIsFormOpen] = useState(false);
//   const [newSector, setNewSector] = useState('');
//   const [newCost, setNewCost] = useState('');
//   const [editIndex, setEditIndex] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 5;

//   const dispatch = useDispatch();
//   const { data, loading, error } = useSelector((s) => s.getallGraphdata);

//   useEffect(() => {
//     dispatch(getallGraphdata()); // Fetch data on mount
//   }, [dispatch]);

//   // Ensure data is an array before using slice
//   const graphData = Array.isArray(data) ? data : [];

//   // Pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = graphData.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(graphData.length / itemsPerPage);

//   const handlePageChange = (pageNumber) => {
//     setCurrentPage(pageNumber);
//   };

//   function handleClosemodalMaster (){
//     setIsFormOpen(false);
//   }

//   return (
//     <div className="flex justify-center p-4">
//       <div className="max-w-4xl w-full rounded-3xl shadow-lg bg-gray-100 overflow-hidden">
//         <div className="flex justify-between items-center p-4 bg-gray-200">
//           <h2 className="text-xl font-bold text-gray-800">Government Spending Sectors</h2>
//           <button
//             onClick={() => setIsFormOpen(true)}
//             className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
//           >
//             Add Sector
//           </button>

//         </div>
//         {isFormOpen ? <AddSector_Graph handleCloseModal={handleClosemodalMaster} /> : null}

//         {/* Table */}
//         <div className="p-4">
//           {loading ? (
//             <p>Loading...</p>
//           ) : error ? (
//             <p className="text-red-500">Error loading data.</p>
//           ) : (
//             <table className="w-full text-left">
//               <thead>
//                 <tr className="bg-gray-200">
//                   <th className="p-4 text-gray-800 font-bold">Sector</th>
//                   <th className="p-4 text-gray-800 font-bold">Cost (in billions)</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {currentItems.map((row, index) => (
//                   <tr key={row._id} className="border-b border-gray-300 last:border-none">
//                     <td className="p-4 text-gray-800">{row.sector}</td>
//                     <td className="p-4 text-gray-800">{row.cost}</td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           )}
//         </div>

//         {/* Pagination */}
//         <div className="flex justify-between items-center p-4 bg-gray-200">
//           <button
//             onClick={() => handlePageChange(currentPage - 1)}
//             disabled={currentPage === 1}
//             className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
//           >
//             Previous
//           </button>
//           <span className="text-gray-800">
//             Page {currentPage} of {totalPages}
//           </span>
//           <button
//             onClick={() => handlePageChange(currentPage + 1)}
//             disabled={currentPage === totalPages}
//             className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
//           >
//             Next
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default GraphTable;
// ..............................................................

// import React, { useState, useEffect } from 'react';
// import { Pencil, Trash2, X, CheckCircle, Edit, ChevronLeft } from 'lucide-react';
// import { getallGraphdata } from '../../FeatureRedux/GraphData/showGraphdata';
// import { useDispatch, useSelector } from 'react-redux';

// const GraphTable = () => {
//   const [graphData, setGraphData] = useState(() => {
//     const savedData = localStorage.getItem('graphData');
//     return savedData ? JSON.parse(savedData) : [
//       { sector: 'Railway', cost: 120 },
//       { sector: 'Education', cost: 90 },
//       { sector: 'Healthcare', cost: 80 },
//       { sector: 'Defense', cost: 70 },
//       { sector: 'Infrastructure', cost: 60 },
//       { sector: 'Agriculture', cost: 50 },
//       { sector: 'Technology', cost: 40 },
//       { sector: 'Energy', cost: 30 },
//       { sector: 'Environment', cost: 20 },
//     ];
//   });



//   const [isFormOpen, setIsFormOpen] = useState(false);
//   const [newSector, setNewSector] = useState('');
//   const [newCost, setNewCost] = useState('');
//   const [editIndex, setEditIndex] = useState(null);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [showUpdateModal, setShowUpdateModal] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 5;

//   // Save data to localStorage whenever graphData changes
//   useEffect(() => {
//     localStorage.setItem('graphData', JSON.stringify(graphData));
//   }, [graphData]);



//   const {data, loading, error, isSuccess} = useSelector((s)=>s.getallGraphdata);
//   const dispatch = useDispatch();
//   console.log(`This is the data i want to display ${JSON.stringify(data , null , " ")}`);
//   useEffect(()=>{
//     dispatch(getallGraphdata());
//   },[dispatch])

//   // Pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = graphData.slice(indexOfFirstItem, indexOfLastItem);

//   const totalPages = Math.ceil(graphData.length / itemsPerPage);

//   const handlePageChange = (pageNumber) => {
//     setCurrentPage(pageNumber);
//   };

//   const handleAddSector = () => {
//     if (newSector && newCost) {
//       const updatedData = [...graphData, { sector: newSector, cost: parseFloat(newCost) }];
//       setGraphData(updatedData);
//       setIsFormOpen(false);
//       setNewSector('');
//       setNewCost('');
//       setShowSuccessModal(true);
//     }
//   };

//   const handleEdit = (index) => {
//     setEditIndex(index);
//     setNewSector(graphData[index].sector);
//     setNewCost(graphData[index].cost.toString());
//     setIsFormOpen(true);
//   };

//   const handleUpdateSector = () => {
//     if (newSector && newCost && editIndex !== null) {
//       const updatedData = [...graphData];
//       updatedData[editIndex] = { sector: newSector, cost: parseFloat(newCost) };
//       setGraphData(updatedData);
//       setIsFormOpen(false);
//       setNewSector('');
//       setNewCost('');
//       setEditIndex(null);
//       setShowUpdateModal(true);
//     }
//   };

//   const handleDelete = (index) => {
//     const updatedData = graphData.filter((_, i) => i !== index);
//     setGraphData(updatedData);
//   };

//   const closeSuccessModal = () => setShowSuccessModal(false);
//   const closeUpdateModal = () => setShowUpdateModal(false);

//   return (
//     <div className="flex justify-center p-4">
//       <div className="max-w-4xl w-full rounded-3xl shadow-lg bg-gray-100 overflow-hidden">
//         <div className="flex justify-between items-center p-4 bg-gray-200">
//           <button
//             onClick={() => window.history.back()}
//             className="flex items-center text-gray-800 hover:text-gray-600"
//           >
//             {/* <ChevronLeft className="w-6 h-6 mr-2" /> */}
            
//           </button>
//           <button
//             onClick={() => setIsFormOpen(true)}
//             className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
//           >
//             Add Sector
//           </button>
//         </div>
//         <h2 className="text-center text-xl font-bold text-gray-800 py-4 bg-gray-200">
//           Government Spending Sectors Table
//         </h2>
//         <div className="p-4">
//           {isFormOpen && (
//             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//               <div className="bg-white p-6 rounded-lg w-96">
//                 <div className="flex justify-between items-center mb-4">
//                   <h3 className="text-lg font-bold">{editIndex !== null ? 'Edit Sector' : 'Add Sector'}</h3>
//                   <button onClick={() => setIsFormOpen(false)}>
//                     <X size={20} className="text-gray-600" />
//                   </button>
//                 </div>
//                 <input
//                   type="text"
//                   placeholder="Sector"
//                   value={newSector}
//                   onChange={(e) => setNewSector(e.target.value)}
//                   className="w-full p-2 border border-gray-300 rounded-lg mb-4"
//                 />
//                 <input
//                   type="number"
//                   placeholder="Cost (in billions)"
//                   value={newCost}
//                   onChange={(e) => setNewCost(e.target.value)}
//                   className="w-full p-2 border border-gray-300 rounded-lg mb-4"
//                 />
//                 <button
//                   onClick={editIndex !== null ? handleUpdateSector : handleAddSector}
//                   className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
//                 >
//                   {editIndex !== null ? 'Update' : 'Add'}
//                 </button>
//               </div>
//             </div>
//           )}
//         </div>
//         <table className="w-full text-left">
//           <thead>
//             <tr className="bg-gray-200">
//               <th className="p-4 text-gray-800 font-bold">Sector</th>
//               <th className="p-4 text-gray-800 font-bold">Cost (in billions)</th>
//               <th className="p-4 text-gray-800 font-bold">Action</th>
//             </tr>
//           </thead>
//           <tbody>
//             {currentItems.map((row, index) => (
//               <tr key={index} className="border-b border-gray-300 last:border-none">
//                 <td className="p-4 text-gray-800">{row.sector}</td>
//                 <td className="p-4 text-gray-800">{row.cost}</td>
//                 <td className="p-4">
//                   <button
//                     onClick={() => handleEdit(indexOfFirstItem + index)}
//                     className="text-blue-500 mr-3"
//                   >
//                     <Pencil size={20} />
//                   </button>
//                   <button
//                     onClick={() => handleDelete(indexOfFirstItem + index)}
//                     className="text-red-500"
//                   >
//                     <Trash2 size={20} />
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//         <div className="flex justify-between items-center p-4 bg-gray-200">
//           <button
//             onClick={() => handlePageChange(currentPage - 1)}
//             disabled={currentPage === 1}
//             className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
//           >
//             Previous
//           </button>
//           <span className="text-gray-800">
//             Page {currentPage} of {totalPages}
//           </span>
//           <button
//             onClick={() => handlePageChange(currentPage + 1)}
//             disabled={currentPage === totalPages}
//             className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
//           >
//             Next
//           </button>
//         </div>
//       </div>

//       {/* Success Modal */}
//       {showSuccessModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-8 w-96 relative">
//             <button onClick={closeSuccessModal} className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full">
//               <X className="w-6 h-6" />
//             </button>
//             <div className="flex flex-col items-center">
//               <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
//               <h2 className="text-2xl font-bold mb-2">Congratulations!</h2>
//               <p className="text-gray-600 text-center">Sector has been successfully Added.</p>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Update Modal */}
//       {showUpdateModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-8 w-96 relative">
//             <button onClick={closeUpdateModal} className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full">
//               <X className="w-6 h-6" />
//             </button>
//             <div className="flex flex-col items-center">
//               <Edit className="w-12 h-12 text-blue-500 mb-4" />
//               <h2 className="text-2xl font-bold mb-2">Successfully Updated!</h2>
//               <p className="text-gray-600 text-center">Sector has been successfully updated.</p>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default GraphTable;